import React from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RegisterGoogleAutoComplete from "./RegisterGoogleAutoComplete";
import { primaryColor, secondaryColor } from "../hooks/useFirstTimeRegister";
import { isRegisterLawFirmLocationComplete } from "../../Internal User/Shared Management Pages/Law Firm Management/lawFirmLocationFormConfig";

export default function RegisterLawFirmCreationDialog({ p }) {
  const {
    openLawFirmDialog,
    handleCloseLawFirmDialog,
    newLawFirm,
    setNewLawFirm,
    handleLawFirmAddressCallback,
    allCities,
    allStates,
    loadingCitiesStates,
    handleAddLawFirm,
    loadingLawFirms,
    handleNewLawFirmPhoneChange,
  } = p;

  return (
    <>
      {/* Law Firm Creation Dialog */}
      <Dialog open={openLawFirmDialog} onClose={handleCloseLawFirmDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: primaryColor }}>
          Create New Law Firm & Location
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Law Firm Information */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Law Firm Information
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Law Firm Name
              </Typography>
              <Tooltip title="Enter the name of the law firm" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.name}
              onChange={(e) => setNewLawFirm({ ...newLawFirm, name: e.target.value })}
              placeholder="Enter law firm name"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Website (Optional)
              </Typography>
              <Tooltip title="Enter the law firm's website URL" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.website}
              onChange={(e) => setNewLawFirm({ ...newLawFirm, website: e.target.value })}
              placeholder="https://www.lawfirm.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Location Information */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Main Location Information
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Search Address
              </Typography>
              <Tooltip title="Search for the law firm's main office address" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <RegisterGoogleAutoComplete
              key={`law-firm-address-${openLawFirmDialog}`}
              placeholder="Search Office Address"
              callback={handleLawFirmAddressCallback}
            />

            {/* Individual Address Fields */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, mt: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Street Address
              </Typography>
              <Tooltip title="Street address of the law firm" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.address}
              onChange={(e) => setNewLawFirm(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Street address"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    City
                  </Typography>
                  <Tooltip title="City where the law firm is located" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  options={allCities}
                  loading={loadingCitiesStates}
                  value={
                    allCities.includes(newLawFirm.city) ? newLawFirm.city : null
                  }
                  onChange={(_, newValue) =>
                    setNewLawFirm((prev) => ({ ...prev, city: newValue || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      placeholder="City"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                      }}
                      slotProps={{ input: { ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCitiesStates ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    State
                  </Typography>
                  <Tooltip title="State where the law firm is located" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  options={allStates}
                  loading={loadingCitiesStates}
                  value={
                    allStates.includes(newLawFirm.state) ? newLawFirm.state : null
                  }
                  onChange={(_, newValue) =>
                    setNewLawFirm((prev) => ({ ...prev, state: newValue || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      placeholder="State"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                      }}
                      slotProps={{ input: { ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCitiesStates ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    ZIP Code
                  </Typography>
                  <Tooltip title="ZIP code of the law firm's location" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={newLawFirm.zip}
                  onChange={(e) => setNewLawFirm(prev => ({ ...prev, zip: e.target.value }))}
                  placeholder="ZIP"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                      backgroundColor: "rgba(249, 249, 249, 0.8)",
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: primaryColor,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "text.secondary",
                      opacity: 0.7,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Phone Number (Optional)
              </Typography>
              <Tooltip title="Enter the main office phone number" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.phone}
              onChange={handleNewLawFirmPhoneChange}
              placeholder="(555) 123-4567"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseLawFirmDialog}
            variant="outlined"
            sx={{
              borderRadius: "0px",
              py: 1.2,
              px: 3,
              fontSize: "0.9rem",
              fontWeight: 600,
              borderColor: primaryColor,
              color: primaryColor,
              borderWidth: "2px",
              "&:hover": {
                borderColor: secondaryColor,
                backgroundColor: "rgba(51, 51, 51, 0.08)",
                borderWidth: "2px",
              },
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddLawFirm}
            variant="contained"
            disabled={loadingLawFirms || !isRegisterLawFirmLocationComplete(newLawFirm)}
            sx={{
              borderRadius: "0px",
              py: 1.2,
              px: 3,
              fontSize: "0.9rem",
              fontWeight: 600,
              bgcolor: primaryColor,
              color: "white",
              boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
              "&:hover": {
                bgcolor: secondaryColor,
                boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                bgcolor: "rgba(51, 51, 51, 0.3)",
                color: "rgba(255, 255, 255, 0.5)",
              },
              textTransform: "none",
              transition: "all 0.3s ease",
            }}
          >
            {loadingLawFirms ? <CircularProgress size={20} color="inherit" /> : "Create Law Firm & Location"}
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
}
